import { Box, Button, Link, Typography } from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import type { SponsorCampaign } from "../campaignTypes";
import { PORTAL_MAX_WIDTH, bodySx, portalFont } from "../partnerTheme";

export default function CampaignClosingCTA({
  closing,
}: {
  closing: SponsorCampaign["closing"];
}) {
  return (
    <Box
      component="section"
      sx={{
        maxWidth: PORTAL_MAX_WIDTH,
        mx: "auto",
        px: { xs: 2, md: 4 },
        py: { xs: 10, md: 14 },
        textAlign: "center",
      }}
    >
      <Typography
        component="h2"
        sx={{
          fontFamily: portalFont,
          fontWeight: 700,
          color: "var(--portal-text)",
          fontSize: "clamp(2rem, 3vw + 1rem, 3.25rem)",
          letterSpacing: "-0.03em",
          mb: 2,
        }}
      >
        {closing.thankYouHeadline}
      </Typography>
      <Typography sx={{ ...bodySx, maxWidth: 640, mx: "auto", mb: 3 }}>
        {closing.summary}
      </Typography>
      <Box
        sx={{
          maxWidth: 720,
          mx: "auto",
          px: { xs: 2.5, md: 3.5 },
          py: { xs: 2.5, md: 3 },
          borderRadius: "var(--portal-radius)",
          border: "1px solid var(--portal-accent)",
          bgcolor: "color-mix(in srgb, var(--portal-accent) 10%, var(--portal-surface))",
          mb: closing.nextStep || (closing.ctaLabel && closing.ctaHref) ? 3 : 4,
        }}
      >
        <Typography
          sx={{
            fontFamily: portalFont,
            color: "var(--portal-text)",
            fontWeight: 600,
            fontSize: { xs: "1rem", md: "1.125rem" },
            lineHeight: 1.5,
          }}
        >
          {closing.highlightCallout}
        </Typography>
      </Box>
      {closing.nextStep ? (
        <Typography sx={{ ...bodySx, mb: 3.5 }}>{closing.nextStep}</Typography>
      ) : null}
      {closing.ctaLabel && closing.ctaHref ? (
        <Button
          href={closing.ctaHref}
          sx={{
            fontFamily: portalFont,
            fontWeight: 700,
            textTransform: "none",
            bgcolor: "var(--portal-accent)",
            color: "#fff",
            borderRadius: "999px",
            px: 3.5,
            py: 1.5,
            fontSize: "1rem",
            mb: 4,
            "&:hover": { bgcolor: "var(--portal-accent)", filter: "brightness(1.08)" },
            "&:focus-visible": { outline: "2px solid #fff", outlineOffset: 3 },
          }}
        >
          {closing.ctaLabel}
        </Button>
      ) : null}
      <Box>
        <Typography
          sx={{
            fontFamily: portalFont,
            fontWeight: 700,
            color: "var(--portal-text)",
            mb: 0.35,
          }}
        >
          {closing.contact.name}
        </Typography>
        <Typography sx={{ fontFamily: portalFont, color: "var(--portal-text-muted)", mb: 1 }}>
          {closing.contact.role}
        </Typography>
        <Link
          href={`mailto:${closing.contact.email}`}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            fontFamily: portalFont,
            color: "var(--portal-text)",
            fontWeight: 600,
            textDecoration: "underline",
            textUnderlineOffset: 3,
            "&:focus-visible": {
              outline: "2px solid var(--portal-accent)",
              outlineOffset: 2,
            },
          }}
        >
          <MailOutlineIcon sx={{ fontSize: 18 }} />
          {closing.contact.email}
        </Link>
      </Box>
    </Box>
  );
}
